import { usePlaygroundDispatch } from "./usePlaygroundContext";
import {
  setChatbotProps,
  setPlaygroundPopoverOpen,
  updateUserView,
  setForceNewSession,
} from "./playgroundSlice";
import { useCallback } from "react";
import { ChatbotProps } from "./types";
import {
  PLAYGROUND_SESSION_CREATED_EVENT,
  PlaygroundSessionCreatedDetail,
} from "./hooks/usePlaygroundRoute";

/**
 * Hook that provides common playground actions
 * Handles the pattern of opening global playground or falling back to local behavior
 */
export const useGlobalPlaygroundActions = () => {
  const playgroundDispatch = usePlaygroundDispatch();

  /**
   * Opens the playground with chatbot props
   */
  const openPlayground = useCallback(
    ({
      chatbotProps,
      context,
    }: {
      /** Chatbot props including initialMessage and helloMessage */
      chatbotProps: Partial<ChatbotProps>;
      /** Additional context to extend the page-level user view */
      context?: Record<string, unknown>;
    }) => {
      // Register one-time event listener to apply data when session is created
      // Using closures to capture context and chatbotProps
      window.addEventListener(
        PLAYGROUND_SESSION_CREATED_EVENT,
        (event: Event) => {
          const customEvent = event as CustomEvent<PlaygroundSessionCreatedDetail>;
          const { sessionId } = customEvent.detail;

          console.log("openPlayground: Applying pending data to new session", sessionId, {
            context,
            chatbotProps,
          });

          // Apply pending context
          if (context) {
            playgroundDispatch(
              updateUserView({
                sessionId,
                userView: context,
              })
            );
          }

          setTimeout(() => {
            // Apply pending chatbot props
            if (chatbotProps) {
              playgroundDispatch(setChatbotProps(chatbotProps));
            }
          }, 10);
        },
        { once: true }
      );

      playgroundDispatch(setForceNewSession(Date.now()));
      playgroundDispatch(setPlaygroundPopoverOpen(true));
    },
    [playgroundDispatch]
  );

  return {
    openPlayground,
  };
};
